import type { IListing } from "@/src/shared/types/listing";

// Price calculation result type
interface IPriceModel {
  discountPrice?: number;
  perDayPrice: number;
  totalPrice: number;
}

// Price calculation function with proper typing
const priceCheckingModelV2 = (
  hotelList: IListing | undefined,
): IPriceModel | null => {
  if (!hotelList) return null;
  const price = hotelList.price ?? 0;
  // Null and 0 behave alike in every comparison below, as they always did.
  const averagePrice = hotelList.average_price ?? 0;
  const showablePrice = hotelList.showable_price;
  const beforeDiscount = hotelList.before_discount ?? 0;
  let discountPrice: number | undefined;
  // Every branch below assigns these; `price` only covers the unreachable fall-through.
  let perDayPrice = price;
  let totalPrice = price;
  const weekendPrice = hotelList.weekend_price;

  // Case 1: No average price provided
  if (!hotelList.average_price) {
    if (showablePrice > price) {
      discountPrice = showablePrice;
    }
    perDayPrice = price;
    totalPrice = price;
  }

  // Case 2: price == average_price
  else if (price === averagePrice) {
    if (showablePrice > price) {
      discountPrice = showablePrice;
    }
    perDayPrice = price;
    totalPrice = averagePrice;
  }
  // Case 3: price > average_price
  else if (averagePrice && price > averagePrice) {
    discountPrice = price;
    perDayPrice = averagePrice;
    totalPrice = averagePrice;
  }
  // Case 4: before_discount > average_price
  else if (averagePrice && beforeDiscount > averagePrice) {
    discountPrice = beforeDiscount;
    perDayPrice = averagePrice;
    totalPrice = averagePrice;
  }
  // Case 5: average_price > price
  else if (averagePrice > price) {
    perDayPrice = averagePrice;
    totalPrice = averagePrice;
  }

  // Optional: Use weekend price if it's lower
  if (weekendPrice && weekendPrice < perDayPrice) {
    perDayPrice = averagePrice > 0 ? averagePrice : price;
    totalPrice = averagePrice > 0 ? averagePrice : price;
  }

  return { discountPrice, perDayPrice, totalPrice };
};

// Date difference calculation function
const dateDifferenceV2 = (from: string, to: string): number => {
  const fromDate = new Date(from);
  const toDate = new Date(to);
  const timeDiff = toDate.getTime() - fromDate.getTime();
  const dayDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
  return dayDiff > 0 ? dayDiff : 1;
};

export { priceCheckingModelV2, dateDifferenceV2 };
